import React, { useState } from 'react'

import type { DateNameEnum, DateRange } from '@ztechapps/common.utils'
import type { DateType } from '@ztechapps/common.utils'
import { DateNameValue, toDate, isCustomDateRange, findKeyByDateRange } from '@ztechapps/common.utils'
import { useUserTimeZone } from '@ztechapps/common.hook'

import { DateFilter } from '../../DateFilter'
import { useTableFilterContext } from './Context'
import { FilterContainerBodyLayout } from './FilterContainer'
import type { TableFilterProps } from './types'

/**
 * Helper function to extract dates from stored custom range filter
 * Converts ISO timestamps or Date objects to YYYY-MM-DD format for DateTimeInput
 * Uses the user's timezone to correctly display the date that was originally selected
 * @precondition storedRange must be a valid DateRange with $gte and $lte properties
 */
const parseCustomRange = (
  storedRange: DateRange | null | undefined,
  timeZone?: string
): { startDate: string; endDate: string } => {
  if (!storedRange) {
    return { startDate: '', endDate: '' }
  }

  // Extract date part (YYYY-MM-DD) from ISO timestamps, converting to user's timezone
  const startDate = storedRange.$gte ? toDate(storedRange.$gte, timeZone).format('YYYY-MM-DD') : ''
  const endDate = storedRange.$lte ? toDate(storedRange.$lte, timeZone).format('YYYY-MM-DD') : ''

  return { startDate, endDate }
}

export const DateTableFilter = ({ field }: TableFilterProps) => {
  const { onFilter, getStoredFilter, setShowFilterContainer } = useTableFilterContext()
  const timeZone = useUserTimeZone()
  const storedRange = getStoredFilter() as DateRange

  const initialValue = findKeyByDateRange(storedRange?.$gte, storedRange?.$lte)

  // Initialize custom range state from stored filter if it's a custom range
  const isCustom = isCustomDateRange(storedRange)
  const initialCustomRange = isCustom ? parseCustomRange(storedRange, timeZone) : { startDate: '', endDate: '' }

  const [dateName, setDateName] = useState<DateNameEnum>(initialValue)
  const [startDate, setStartDate] = useState<string>(initialCustomRange.startDate)
  const [endDate, setEndDate] = useState<string>(initialCustomRange.endDate)

  // Validation: check if filter can be applied
  const isFilterValid = () => {
    // Valid if preset is selected
    if (dateName) return true
    // Valid if both custom dates are filled and valid
    if (startDate && endDate) {
      const start = toDate(startDate, timeZone, { keepLocalTime: true })
      const end = toDate(endDate, timeZone, { keepLocalTime: true })
      return start.isValid() && end.isValid()
    }
    // Invalid if only one date is filled or neither
    return false
  }

  const filterNow = (dateNameArg: DateNameEnum = null) => {
    let extraFilter
    if (startDate && endDate && !dateNameArg) {
      // Custom range filter with absolute dates
      // Use user's timezone for consistent date boundaries (same as preset filters)
      let startMoment = toDate(startDate, timeZone, { keepLocalTime: true }).startOf('day')
      let endMoment = toDate(endDate, timeZone, { keepLocalTime: true }).endOf('day')

      if (endMoment.isBefore(startMoment)) {
        // Swap the moments
        const temp = startMoment
        startMoment = endMoment
        endMoment = temp
      }

      const start = startMoment.toISOString()
      const end = endMoment.toISOString()

      extraFilter = { [field.name]: { $gte: start, $lte: end } }
    } else {
      // Preset filter (existing behavior)
      dateNameArg = dateNameArg ?? dateName
      extraFilter = { [field.name]: DateNameValue[dateNameArg] }
    }
    onFilter(extraFilter)
  }

  const isActive = (current: DateNameEnum) => current === dateName

  const onClickListItem = (dateNameArg: DateNameEnum = null) => {
    setDateName(dateNameArg)
    // Clear custom range inputs when preset is selected
    setStartDate('')
    setEndDate('')
    filterNow(dateNameArg)
    setShowFilterContainer(false)
  }

  const handleStartDateChange = (date: DateType) => {
    setStartDate(date as string)
    // Clear preset selection when custom range is entered
    setDateName(null)
  }

  const handleEndDateChange = (date: DateType) => {
    setEndDate(date as string)
    // Clear preset selection when custom range is entered
    setDateName(null)
  }

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && isFilterValid()) {
      filterNow()
      setShowFilterContainer(false)
    }
  }

  // Only pass onOk when filter is valid
  const handleOk = isFilterValid() ? () => filterNow() : undefined

  return (
    <FilterContainerBodyLayout onOk={handleOk}>
      <DateFilter
        onClickListItem={onClickListItem}
        isActive={isActive}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={handleStartDateChange}
        onEndDateChange={handleEndDateChange}
        onKeyPress={handleKeyPress}
      />
    </FilterContainerBodyLayout>
  )
}
